import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC} from 'react';
import {
  components,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from 'react-select';

import {ServiceAccountAuthMethod} from '../../generated/graphql';
import {ServiceAccountAuthPill} from './ServiceAccountAuthPill';

interface ServiceAccountAuthOption {
  value: ServiceAccountAuthMethod;
  description: string;
  label: string;
  isDisabled?: boolean;
}

interface ServiceAccountAuthOptionsProps {
  selectedAuthMethod: ServiceAccountAuthMethod | undefined;
  setSelectedAuthMethod: (option: ServiceAccountAuthMethod) => void;
  isFederationEnabled: boolean;
}

export const ServiceAccountAuthOptions: FC<ServiceAccountAuthOptionsProps> = ({
  selectedAuthMethod,
  setSelectedAuthMethod,
  isFederationEnabled,
}) => {
  const options: ServiceAccountAuthOption[] = [
    {
      value: ServiceAccountAuthMethod.ApiKey,
      description: 'Generate an API key to authenticate your service account.',
      label: 'Generate API key',
    },
  ];

  if (isFederationEnabled) {
    options.push({
      value: ServiceAccountAuthMethod.Federated,
      description:
        'Use identity federation to authenticate your service account via an external identity provider.',
      label: 'Federated identity',
    });
  }

  const selectedOption = options.find(o => o.value === selectedAuthMethod);

  return (
    <Select<ServiceAccountAuthOption>
      value={selectedOption}
      options={options}
      placeholder="Select an authentication method"
      id="service-account-auth-options"
      components={{
        Option: ServiceAccountAuthOptionComp,
        SingleValue: ServiceAccountAuthValueComp,
      }}
      onChange={(o: SingleValue<ServiceAccountAuthOption>) =>
        setSelectedAuthMethod(o?.value ?? ServiceAccountAuthMethod.ApiKey)
      }
    />
  );
};

const ServiceAccountAuthOptionComp: React.FC<
  OptionProps<ServiceAccountAuthOption, false>
> = ({children, ...props}) => {
  const {value, description} = props.data;

  return (
    <components.Option {...props}>
      <Tailwind>
        <div className="flex items-center">
          {children}
          <ServiceAccountAuthPill authMethod={value} />
        </div>
        <span className="text-sm text-moon-500">{description}</span>
      </Tailwind>
    </components.Option>
  );
};

const ServiceAccountAuthValueComp: React.FC<
  SingleValueProps<ServiceAccountAuthOption, false>
> = ({children, ...props}) => {
  const {value} = props.data;
  return (
    <components.SingleValue {...props}>
      <Tailwind>
        <div className="flex items-center">
          {children}
          <ServiceAccountAuthPill authMethod={value} />
        </div>
      </Tailwind>
    </components.SingleValue>
  );
};
